import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';
import { Manual } from '../../../api/model';
import * as sessionStorageUtils from '../../../utils/sessionStorage';
import { ManualRow } from './ManualRow';
import { ManualUpload } from './ManualUpload';
import { ManualDownloadAll } from './ManualDownloadAll';
import PageNavi from '../../modules/PageNavi';

type Props = {
  manuals: Manual[];
  onSearch: () => void;
};
const N_DATA_PER_PAGE = 50;

type Inputs = {
  comment: string;
};

function ManualList({ manuals, onSearch }: Props) {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();

  const { register, handleSubmit } = useForm<Inputs>({
    defaultValues: {
      comment: params.get('comment') || '',
    },
  });
  const onSubmit: SubmitHandler<Inputs> = (data) => {
    setParams({
      comment: data.comment,
    });
  };

  // 現在表示中のページ番号
  const [currentPage, setCurrentPage] = useState(sessionStorageUtils.get('Masters.CurrentPage', 1));

  // 初回ロード中かどうか
  const refIsFirstLoading = useRef(true);

  // 現在ページを変更する関数
  const changeCurrentPage = useCallback(
    (next: number) => {
      sessionStorageUtils.set('Masters.CurrentPage', next);
      setCurrentPage(next);
    },
    [setCurrentPage]
  );

  // データが変更されたときの動作
  useEffect(() => {
    if (manuals != null) {
      if (refIsFirstLoading.current) {
        // 初回ロード完了時は初回ロードが完了したことを設定する
        refIsFirstLoading.current = false;
      } else {
        // 初回ロード以外にデータが変更されたときは、現在ページを 1 ページにする
        setCurrentPage(1);
      }
    }
  }, [manuals]);

  // 現在のページに表示する一覧
  const filteredManuals = useMemo(
    () => manuals.slice(N_DATA_PER_PAGE * (currentPage - 1), N_DATA_PER_PAGE * currentPage),
    [currentPage, manuals]
  );

  return (
    <section>
      <div className="ttlsep prolist">
        <div className="left">
          <h3 className="trig">
            <span>{t('マニュアル一覧')}</span>
          </h3>
        </div>
        <div className="right">
          <ul>
            <ManualUpload onSearch={onSearch} />
            <ManualDownloadAll manuals={filteredManuals} />
          </ul>
        </div>
      </div>

      <div className="wfr in-pgnavi">
        <form onSubmit={handleSubmit(onSubmit)}>
          <table className="table45">
            <tbody>
              <tr>
                <th>
                  {t('資料')}
                  <span className="search_box">
                    <input type="text" {...register('comment')} />
                  </span>
                </th>
                <th>{t('最終更新日')}</th>
                <th>{t('更新者')}</th>
                <th>{t('コメント')}</th>
              </tr>
              {filteredManuals.map((manual) => (
                <ManualRow key={manual.manual_id} manual={manual} onSearch={onSearch} />
              ))}
            </tbody>
          </table>
        </form>

        <PageNavi
          current={currentPage}
          pageCount={Math.ceil(manuals.length / (N_DATA_PER_PAGE * 1.0))}
          onChangePage={changeCurrentPage}
        />
      </div>
    </section>
  );
}

export { ManualList };
